import { Pharmacy } from '../pharmacy.service';
import { Device } from './../../models/device';
import { VehicleStatuses } from './../../models/vehicle-statuses.model';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AppStoreService {
  constructor() {}

  decimalToHexString = (decimalValue: number) => {
    if (decimalValue < 0) {
      decimalValue = 0xffff + decimalValue + 1;
    }

    return decimalValue.toString(16).toUpperCase();
  };

  toStringOrUndefined = (value) => {
    return value !== undefined ? String(value) : value;
  };

  mapFromDao = (vehicle, vehiclesDescriptions, extColumns) => {
    const currentVehicleDescription = vehiclesDescriptions.find(
      (vehicleDescription) =>
        vehicleDescription.id === parseInt(vehicle.vehicle_id, 10)
    );
    return {
      vehicle: this.toStringOrUndefined(vehicle.vehicle_id),
      lat: this.toStringOrUndefined(vehicle.latitude),
      lon: this.toStringOrUndefined(vehicle.longitude),
      speed: this.toStringOrUndefined(vehicle.speed),
      dist: this.toStringOrUndefined(vehicle.distance),
      hdg: this.toStringOrUndefined(vehicle.heading),
      fuel: this.toStringOrUndefined(vehicle.fuel),
      sat: this.toStringOrUndefined(vehicle.satellites),
      name: currentVehicleDescription
        ? currentVehicleDescription.custom_description ||
          currentVehicleDescription.description
        : 'Unknown',
      type: currentVehicleDescription
        ? currentVehicleDescription.type
        : 'osobowy',
      dallas: this.toStringOrUndefined(vehicle.dallas_id),
      altdallas: this.toStringOrUndefined(vehicle.alt_dallas_id),
      time: this.toStringOrUndefined(vehicle.timestamp),
      extcolumns: {
        ext: this.mapExtColumns(vehicle, extColumns),
      },
      vstats: { sparam: this.mapSparam(vehicle) },
      statusicons: { icon: vehicle.vehicle_status_icons },
      typeId: currentVehicleDescription
        ? currentVehicleDescription.type_id
        : undefined,
      locked: currentVehicleDescription
        ? currentVehicleDescription.lockState === 'block'
        : false,
      note: {
        value: currentVehicleDescription
          ? currentVehicleDescription.note
          : undefined,
        lastChange: currentVehicleDescription
          ? currentVehicleDescription.noteLastChange
          : undefined,
        lastLogin: currentVehicleDescription
          ? currentVehicleDescription.noteLastLogin
          : undefined,
      },
      battery: vehicle.battery,
      suply: vehicle.suply,
      avgFuelConsumed: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.avgFuelConsumed
        : undefined,
      firstDriverStatistics: vehicle.first_driver_statistics,
      secondDriverStatistics: vehicle.second_driver_statistics,
      groups: currentVehicleDescription.groups,
      sensors: currentVehicleDescription.sensors,
    };
  };

  mapToPharmacy = (pharmacyList: Pharmacy[], vehicle): Pharmacy[] => {
    return pharmacyList.map((pharmacy) => {
      if (vehicle && vehicle.vehicle) {
        if (pharmacy.baseStationsIds.includes(vehicle.vehicle)) {
          return {
            ...pharmacy,
            position: { ...vehicle },
          };
        }
      }
      return pharmacy;
    });
  };

  mapVehicleToPharmacy = (pharmacyList: Pharmacy[], vehicle): Pharmacy[] => {
    if (!pharmacyList) {
      return pharmacyList;
    }
    return pharmacyList.map((pharmacy) => {
      if (vehicle && vehicle.vehicle) {
        if (pharmacy.baseStationsIds.includes(vehicle.vehicle.toString())) {
          const updatedPosition =
            pharmacy && pharmacy.position && Array.isArray(pharmacy.position)
              ? pharmacy.position.map((pos) => {
                  if (pos.vehicle === vehicle.vehicle) {
                    return vehicle;
                  }
                  return pos;
                })
              : { ...vehicle };

          return {
            ...pharmacy,
            position: updatedPosition,
          };
        }
      }
      return pharmacy;
    });
  };

  mapBaseStationsToVehicles(baseStations: Pharmacy[], position): Pharmacy[] {
    if (!baseStations) {
      return [];
    }
    return baseStations.map((baseStation) => {
      const matchedVehicles = position.filter((vehicle) =>
        baseStation.baseStationsIds.includes(vehicle.vehicle)
      );

      return {
        ...baseStation,
        position: matchedVehicles.length > 0 ? matchedVehicles : undefined,
      };
    });
  }

  mapSparam = (vehicle) => {
    const sparam = [];
    sparam.push({
      name: 'todayStartedAt',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.todayStartedAt
        : '0',
      type: 'date',
    });
    sparam.push({
      name: 'rtime',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.rideTime.toString()
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'stime',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.idleTime.toString()
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'cstime',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.maxIdleTime.toString()
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'ptime',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.parkTime.toString()
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'rptime',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.realParkTime.toString()
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'dist',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.distance.toString()
        : '0',
      type: 'float',
    });
    sparam.push({
      name: 'stops',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.starts.toString()
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'vavg',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.avgSpeed.toFixed(2)
        : '0',
      type: 'integer',
    });
    sparam.push({
      name: 'maxSpeed',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.maxSpeed.toString()
        : '0',
      type: 'integer',
    });
    if (vehicle.vehicle_statistics && vehicle.vehicle_statistics.drivingStyle) {
      sparam.push({
        name: 'drivingStyle',
        value: vehicle.vehicle_statistics.drivingStyle.toString(),
        type: 'integer',
      });
    }
    sparam.push({
      name: 'over90Percentage',
      value: vehicle.vehicle_statistics
        ? vehicle.vehicle_statistics.over90Percentage.toFixed(0)
        : '0',
      type: 'integer',
    });
    return sparam;
  };
  mapExtColumns = (vehicle, extColumns) => {
    const extColumnList = [];
    if (
      vehicle.vehicle_statistics &&
      vehicle.vehicle_statistics.ridingOnIdlePercentage
    ) {
      extColumnList.push({
        name: 'ridingOnIdlePercentage',
        value: vehicle.vehicle_statistics.ridingOnIdlePercentage.toFixed(0),
      });
    }
    if (
      vehicle.vehicle_statistics &&
      vehicle.vehicle_statistics.engineBrakingPercentage
    ) {
      extColumnList.push({
        name: 'engineBrakingPercentage',
        value: vehicle.vehicle_statistics.engineBrakingPercentage.toFixed(0),
      });
    }
    for (const key of Object.keys(vehicle)) {
      if (/^CAN.+/.test(key) || /^EXT.+/.test(key)) {
        if (
          extColumns.find(
            (extColumn) => extColumn.name === key && extColumn.visible
          )
        ) {
          extColumnList.push({ name: key, value: vehicle[key] });
        }
      }
    }
    return extColumnList.sort((a, b) => (a.name > b.name ? -1 : 1));
  };
  dslocateMapFromDao = (device, devicesInformation: Device[]) => {
    const deviceInformation = devicesInformation.find(
      (information) => information.id === device.device_id
    );
    return {
      vehicle: device.device_id.toString(),
      lat: this.toStringOrUndefined(device.latitude),
      lon: this.toStringOrUndefined(device.longitude),
      speed: device.ignition ? this.toStringOrUndefined(device.speed) : '255',
      dist: this.toStringOrUndefined(device.distance),
      hdg: this.toStringOrUndefined(device.direction),
      fuel: this.toStringOrUndefined(device.fuel),
      sat: this.toStringOrUndefined(device.satellities),
      name: deviceInformation
        ? deviceInformation.description
          ? deviceInformation.description
          : this.decimalToHexString(device.device_id)
        : this.decimalToHexString(device.device_id),
      type: this.toStringOrUndefined(device.model),
      time: this.toStringOrUndefined(device.time),
      suply: device.supply,
      dallas: undefined,
      altdallas: undefined,
      vstats: { sparam: [] },
      groups: device.groups,
      statusicons: {
        icon: [
          {
            token:
              device.supply > 1
                ? VehicleStatuses.OK
                : VehicleStatuses.POWER_WARNING,
            priority: '1',
          },
        ],
      },
    };
  };
}
